import { VlmSettingsKey } from "../VlmTabToggles";
import { VlmConfigResponse, VlmFlagsByModality } from "./vlmConfigTypes";

export type VlmCaseRow = {
  value: string;
  display_name: string;
  modality?: number | string;
};

export type VlmCaseValueMap = Partial<Record<VlmSettingsKey, string>>;

export const REQUIRED_VLM_SETTINGS_KEYS: VlmSettingsKey[] = [
  "environmentalHazards",
  "lighting",
  "entryway",
  "lossPrevention",
  "leftObjectDetection",
  "customAnalysis",
];

/** Backend case values to try in priority order (first match wins). */
const SINGLE_IMAGE_CASE_CANDIDATES: Record<
  Extract<
    VlmSettingsKey,
    "environmentalHazards" | "lighting" | "entryway" | "customAnalysis"
  >,
  string[]
> = {
  environmentalHazards: [
    "environmental_hazards",
    "environmental_single_img",
    "environmental",
  ],
  lighting: ["lighting_single_img", "lighting"],
  entryway: ["entryway_single_img", "entryway"],
  customAnalysis: ["custom"],
};

const IMAGE_DIFF_CASE_CANDIDATES: Record<
  Extract<VlmSettingsKey, "lossPrevention" | "leftObjectDetection">,
  string[]
> = {
  lossPrevention: ["loss_prevention_diff", "loss_prevention"],
  leftObjectDetection: [
    "left_object_detection_diff",
    "left_object_detection",
  ],
};

const isInternalCaseValue = (value: string): boolean =>
  /^dummy_/i.test(value) ||
  /template/i.test(value) ||
  value === "confirmation" ||
  value === "synonym" ||
  value === "contains_feat" ||
  value === "sys_prompt" ||
  value === "postprocessing_stage2" ||
  value === "cvd_meta_analysis" ||
  /^cvd_for_site_classification/i.test(value) ||
  value === "long_cvd_for_site_classification" ||
  value === "long_site_classification" ||
  value === "site_classification" ||
  value === "img-diff";

const resolveCaseValueFromCandidates = (
  cases: VlmCaseRow[],
  candidates: string[]
): string | undefined => {
  const available = new Set(cases.map((row) => row.value));
  for (const candidate of candidates) {
    if (available.has(candidate)) {
      return candidate;
    }
  }
  return undefined;
};

const resolveSingleImageCases = (
  cases: VlmCaseRow[],
  map: VlmCaseValueMap
) => {
  (
    Object.keys(SINGLE_IMAGE_CASE_CANDIDATES) as Array<
      keyof typeof SINGLE_IMAGE_CASE_CANDIDATES
    >
  ).forEach((settingsKey) => {
    if (map[settingsKey]) {
      return;
    }
    const resolved = resolveCaseValueFromCandidates(
      cases,
      SINGLE_IMAGE_CASE_CANDIDATES[settingsKey]
    );
    if (resolved) {
      map[settingsKey] = resolved;
    }
  });
};

const resolveImageDiffCases = (cases: VlmCaseRow[], map: VlmCaseValueMap) => {
  (
    Object.keys(IMAGE_DIFF_CASE_CANDIDATES) as Array<
      keyof typeof IMAGE_DIFF_CASE_CANDIDATES
    >
  ).forEach((settingsKey) => {
    if (map[settingsKey]) {
      return;
    }
    const resolved = resolveCaseValueFromCandidates(
      cases,
      IMAGE_DIFF_CASE_CANDIDATES[settingsKey]
    );
    if (resolved) {
      map[settingsKey] = resolved;
    }
  });
};

export const getModalityIdsFromConfig = (
  modalities?: VlmConfigResponse["modalities"]
) => ({
  singleImageModalityId: modalities?.single_image?.modality_id,
  imageDiffModalityId: modalities?.image_diff?.modality_id,
});

export const inferCaseKeysFromVlmFlags = (
  vlmFlags?: VlmFlagsByModality
): VlmCaseValueMap => {
  const map: VlmCaseValueMap = {};
  const singleImage = vlmFlags?.single_image ?? {};
  const imageDiff = vlmFlags?.image_diff ?? {};

  Object.keys(singleImage).forEach((caseKey) => {
    if (isInternalCaseValue(caseKey)) {
      return;
    }
    if (/environmental/i.test(caseKey)) {
      map.environmentalHazards = caseKey;
    } else if (/lighting/i.test(caseKey)) {
      map.lighting = caseKey;
    } else if (/entryway/i.test(caseKey)) {
      map.entryway = caseKey;
    } else if (caseKey === "custom") {
      map.customAnalysis = caseKey;
    }
  });

  Object.keys(imageDiff).forEach((caseKey) => {
    if (/loss.prevention/i.test(caseKey)) {
      map.lossPrevention = caseKey;
    } else if (/left.object/i.test(caseKey)) {
      map.leftObjectDetection = caseKey;
    }
  });

  return map;
};

export const buildVlmCaseValueMap = (
  singleImageCases: VlmCaseRow[],
  imageDiffCases: VlmCaseRow[],
  inferredFromFlags: VlmCaseValueMap = {}
): VlmCaseValueMap => {
  const map: VlmCaseValueMap = { ...inferredFromFlags };

  resolveSingleImageCases(singleImageCases, map);
  resolveImageDiffCases(imageDiffCases, map);

  return map;
};

export const buildVlmCaseValueMapFromAllCases = (
  allCases: VlmCaseRow[],
  inferredFromFlags: VlmCaseValueMap = {}
): VlmCaseValueMap => {
  const customerCases = allCases.filter((row) => !isInternalCaseValue(row.value));
  const map: VlmCaseValueMap = { ...inferredFromFlags };

  resolveSingleImageCases(customerCases, map);
  resolveImageDiffCases(customerCases, map);

  return map;
};

export const resolveVlmCaseValueMap = (
  response: VlmConfigResponse,
  singleImageCases: VlmCaseRow[],
  imageDiffCases: VlmCaseRow[],
  allCases: VlmCaseRow[]
): VlmCaseValueMap => {
  const inferred = inferCaseKeysFromVlmFlags(response.vlm_flags);
  let map = buildVlmCaseValueMap(singleImageCases, imageDiffCases, inferred);

  const missingKeys = REQUIRED_VLM_SETTINGS_KEYS.filter((key) => !map[key]);
  if (missingKeys.length > 0 && allCases.length > 0) {
    map = buildVlmCaseValueMapFromAllCases(allCases, map);
  }

  return map;
};
